/**
 * Social links (2026-10-28): clubs type only their handle or page name ("yourclub"), not the whole web address.
 * Stored the same as before, as full https links in myBusinessProfile.socialLink, so the member apps and the club
 * page keep opening them as they are. Twitter is X now (stored under the same "twitter" key).
 *
 *   handleFromLink("instagram", "https://www.instagram.com/yourclub/") -> "yourclub"
 *   linkFromHandle("instagram", "@yourclub")                           -> "https://instagram.com/yourclub"
 * A pasted link of the platform is turned into the handle. A link to another site (e.g. a link page) is kept as it is.
 */

export const SOCIAL_PLATFORMS = {
  instagram: { key: "instagram", label: "Instagram", prefix: "instagram.com/", hosts: ["instagram.com", "instagr.am"], base: "https://instagram.com/" },
  facebook: { key: "facebook", label: "Facebook", prefix: "facebook.com/", hosts: ["facebook.com", "fb.com", "fb.me"], base: "https://facebook.com/" },
  linkedIn: { key: "linkedIn", label: "LinkedIn", prefix: "linkedin.com/company/", hosts: ["linkedin.com"], base: "https://linkedin.com/" },
  twitter: { key: "twitter", label: "X", prefix: "x.com/", hosts: ["x.com", "twitter.com"], base: "https://x.com/" },
  youtube: { key: "youtube", label: "YouTube", prefix: "youtube.com/@", hosts: ["youtube.com", "youtu.be"], base: "https://youtube.com/" },
};

const stripHost = (value) => String(value || "").trim().replace(/^[a-z][a-z0-9+.-]*:\/\//i, "").replace(/^(www\.|m\.|mobile\.)/i, "");
const hostOf = (value) => stripHost(value).split(/[/?#]/)[0].toLowerCase();
const ALL_HOSTS = Object.values(SOCIAL_PLATFORMS).flatMap((p) => p.hosts);
/** a web address (not a handle: "studio.noord" is an Instagram handle, "linktr.ee/club" and "instagram.com" are links) */
const looksLikeLink = (value) =>
  /^[a-z][a-z0-9+.-]*:\/\//i.test(value) ||
  /^www\./i.test(value) ||
  /^[a-z0-9-]+(\.[a-z0-9-]+)+\//i.test(value) ||
  ALL_HOSTS.includes(String(value || "").trim().toLowerCase().replace(/^(m\.|mobile\.)/, ""));

/** Handle / page name as typed or as stored -> what the field shows (no web address, no @). */
export const handleFromLink = (key, value) => {
  const p = SOCIAL_PLATFORMS[key];
  const raw = String(value || "").trim();
  if (!p || !raw) return "";
  if (looksLikeLink(raw)) {
    if (!p.hosts.includes(hostOf(raw))) return raw; // another site: keep the link
    let path = stripHost(raw).slice(hostOf(raw).length).replace(/^\/+/, "");
    const query = key === "facebook" && /^profile\.php\?/i.test(path) ? path.match(/^profile\.php\?[^#]*/i)[0] : "";
    if (query) return query; // facebook.com/profile.php?id=123
    path = path.split(/[?#]/)[0].replace(/\/+$/, "");
    if (key === "linkedIn") return path.replace(/^company\//i, "");
    if (key === "youtube") return path.replace(/^@/, "");
    if (key === "facebook") return path.replace(/^pg\//i, "");
    return path.split("/")[0].replace(/^@/, "");
  }
  return raw.replace(/^@+/, "").replace(/\s+/g, "");
};

/** What the field shows -> what is stored (full https link, or "" when empty). */
export const linkFromHandle = (key, handle) => {
  const p = SOCIAL_PLATFORMS[key];
  const h = String(handle || "").trim();
  if (!p || !h) return "";
  if (looksLikeLink(h)) {
    if (p.hosts.includes(hostOf(h))) return linkFromHandle(key, handleFromLink(key, h));
    return /^https?:\/\//i.test(h) ? h : `https://${h.replace(/^[a-z][a-z0-9+.-]*:\/\//i, "")}`;
  }
  const clean = h.replace(/^@+/, "").replace(/\s+/g, "").replace(/^\/+|\/+$/g, "");
  if (!clean) return "";
  if (key === "linkedIn") return `${p.base}${/[/]/.test(clean) ? clean : `company/${clean}`}`;
  if (key === "youtube") return `${p.base}${/[/]/.test(clean) ? clean : `@${clean}`}`;
  return `${p.base}${clean}`;
};

/** { instagram: "https://…", … } -> { instagram: "yourclub", … } for the form */
export const handlesFromLinks = (socialLink = {}) =>
  Object.fromEntries(Object.keys(SOCIAL_PLATFORMS).map((k) => [k, handleFromLink(k, socialLink?.[k])]));

/** form handles -> socialLink to save (keeps any other keys that were stored) */
export const linksFromHandles = (handles = {}, stored = {}) => ({
  ...(stored || {}),
  ...Object.fromEntries(Object.keys(SOCIAL_PLATFORMS).filter((k) => k in handles).map((k) => [k, linkFromHandle(k, handles[k])])),
});

/** While typing: a pasted link of the platform becomes the handle; anything else stays as typed (minus spaces / @). */
export const cleanHandleInput = (key, value) => {
  const v = String(value || "");
  const p = SOCIAL_PLATFORMS[key];
  if (p && looksLikeLink(v.trim()) && p.hosts.includes(hostOf(v))) {
    const h = handleFromLink(key, v);
    if (h) return h;
  }
  return looksLikeLink(v.trim()) ? v.trim() : v.replace(/^@+/, "").replace(/\s+/g, "");
};
